import { useEffect } from 'react';

import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import i18n from '../../../../../../i18n';
import { useBlikForm } from '../../../../hooks/use-blik-form';
import { InputField } from '../../../../../InputField/InputField';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';
import { apmLatencyTracker } from '../../../../../../utils/apm-latency-tracker';

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .required(i18n.t('checkout.fieldErrors.incompleteEmail'))
    .email(i18n.t('checkout.fieldErrors.invalidEmail')),
});

interface BlikStripePaymentProps {
  onReady: () => void;
  onSubmit: () => void;
}

export function BlikStripePayment({ onReady, onSubmit }: BlikStripePaymentProps) {
  const { checkoutData, selectedPaymentMethod } = useCheckout();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { setFormSubmit, setFormValues, setIsFormValid } = useBlikForm();
  const customer = checkoutData?.customer;

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: {
      email: customer?.email || '',
    },
    onSubmit: () => { },
  });

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Blik', selectedPaymentMethod?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    setTimeout(() => {
      onReady();

      try {
        apmLatencyTracker.endTracking('Blik', selectedPaymentMethod?.id, {
          paymentMethodName: selectedPaymentMethod?.paymentMethodName,
          paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
          provider: 'Stripe',
          type: 'APM',
        });
      } catch (error) {
        console.error('Failed to end APM latency tracking:', error);
      }
    }, 300);
  }, [onReady, selectedPaymentMethod?.id]);

  useEffect(() => {
    setIsFormValid(formik.isValid);
    if (formik.isValid) {
      setFormValues(formik.values);
    }
  }, [formik.isValid, formik.values, setIsFormValid, setFormValues]);

  useEffect(() => {
    setFormSubmit(formik.submitForm);
  }, [formik.submitForm, setFormSubmit]);

  return (
    <PaymentElementContainer
      content={
        <FormikProvider value={formik}>
          <InputField
            name={'email'}
            label={t('email')}
            inputProps={{
              placeholder: t('emailAddress'),
            }}
          />
        </FormikProvider>
      }
    >
      <PayButton onClick={onSubmit} />
    </PaymentElementContainer>
  );
}
